// src/components/admin/AdminDashboardSidebar.jsx
import React, { useState, useEffect } from 'react';
import { useStudentContext } from '../../contexts/StudentContext';
import StaffAvailability from './StaffAvailability';
import ProgramManagement from './ProgramManagement';
import DriverManagement from './DriverManagement';
import WeeklyDriverAssignment from '../student/WeeklyDriverAssignment';
import { shouldShowStudentToday } from '../../utils/dateUtils';
import { useLocation, LOCATIONS } from '../../contexts/LocationContext';
import IncidentReport from '../incident/IncidentReport';
import BHReconcile from './BHReconcile';
import DailyDuties from './DailyDuties';
import AttendanceReport from '../staff/AttendanceReport';

const AdminDashboardSidebar = ({ onNavigate, onLogout, userName }) => {
  const { allStudents } = useStudentContext();
  const [currentView, setCurrentView] = useState('dashboard');
  const [todayStats, setTodayStats] = useState({ scheduledToday: 0, alerts: [] });
  const { currentLocation, setCurrentLocation, locationLabel, locationColor } = useLocation();
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [hrUnlocked, setHrUnlocked] = useState(false);
  const [hrUnlockedAt, setHrUnlockedAt] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingView, setPendingView] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    calculateTodayStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudents]);

  // Auto-lock HR after 30 minutes
  useEffect(() => {
    if (!hrUnlocked || !hrUnlockedAt) return;
    const MS_30_MIN = 30 * 60 * 1000;
    const remaining = MS_30_MIN - (Date.now() - hrUnlockedAt);
    if (remaining <= 0) {
      setHrUnlocked(false);
      setHrUnlockedAt(null);
      return;
    }
    const timer = setTimeout(() => {
      setHrUnlocked(false);
      setHrUnlockedAt(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [hrUnlocked, hrUnlockedAt]);

  const calculateTodayStats = () => {
    const today = new Date();
    const todayString = today.toLocaleDateString('en-US', { weekday: 'long' });
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = String(today.getFullYear()).slice(-2);
    const dateString = `${month}-${day}-${year}`;
    const localDateISO = `${today.getFullYear()}-${month}-${day}`;

    const scheduledToday = allStudents.filter(s => {
      if (s.archived) return false;
      // No programs on weekends
      const todayDow = today.getDay();
      if (todayDow === 0 || todayDow === 6) return false;
      if (s.onBreak === true) {
        const breakStarted = !s.breakStartDate || localDateISO >= s.breakStartDate;
        const breakNotEnded = !s.breakEndDate || localDateISO <= s.breakEndDate;
        if (breakStarted && breakNotEnded) return false;
        if (!s.breakEndDate) return false;
      }
      if (s.programStatus === 'inactive') {
        if (s.inactiveDate) { if (localDateISO >= s.inactiveDate) return false; }
        else return false;
      }
      if (s.startDate && !shouldShowStudentToday(s.startDate)) return false;
      if (s.careDates && Array.isArray(s.careDates)) {
        if (s.careDates.some(care => care.date === dateString)) return true;
      }
      if (s.programDaysPerWeek === '5 days' || s.programDaysPerWeek === '5') {
        return todayString !== 'Saturday' && todayString !== 'Sunday';
      }
      const pickupDays = s.pickupDays || '';
      if (typeof pickupDays === 'string') return pickupDays.toLowerCase().includes(todayString.toLowerCase());
      if (Array.isArray(pickupDays)) return pickupDays.some(d => d.toLowerCase() === todayString.toLowerCase());
      return false;
    });

    const alerts = [];
    const expired = allStudents.filter(s => {
      if (s.archived || !s.submissionDate) return false;
      const sixMonthsAgo = new Date(); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const subDate = s.submissionDate.toDate ? s.submissionDate.toDate() : new Date(s.submissionDate);
      return subDate < sixMonthsAgo;
    });
    if (expired.length > 0) alerts.push(`${expired.length} student(s) with expired forms`);
    setTodayStats({ scheduledToday: scheduledToday.length, alerts });
  };

  const navSections = [
    {
      label: 'Students', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',
      items: [
        { id: 'all-students',       label: 'All Students',        icon: '👥' },
        { id: 'add-student',        label: 'Add New Student',     icon: '➕', sub: true },
        { id: 'archived-students',  label: 'Archived Students',   icon: '📦', sub: true },
        { id: 'todays-students',    label: "Today's Roster",      icon: '📋' },
        { id: 'front-desk',         label: 'Front Desk Check-in', icon: '✅' },
        { id: 'attendance-report',  label: 'Attendance Report',   icon: '📈' },
        { id: 'pause-for-break',    label: 'Pause for Break',     icon: '⏸️', sub: true },
        { id: 'bh-dedup',           label: 'BH Dedup Tool',       icon: '🛠️', sub: true },
      ]
    },
    {
      label: 'Finance', color: '#34d399', bg: 'rgba(52,211,153,0.12)',
      items: [
        { id: 'payment-tracking', label: 'Payment Tracking', icon: '💳' },
      ]
    },
    {
      label: 'Pickup', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',
      items: [
        { id: 'staff-dashboard', label: 'Pickup Monitor', icon: '👁️' },
      ]
    },
    {
      label: 'Staff & Ops', color: '#c084fc', bg: 'rgba(192,132,252,0.12)',
      items: [
        { id: 'locations',             label: 'Locations',          icon: '📍' },
        { id: 'programs',              label: 'Programs',           icon: '🎯' },
        { id: 'staff-availability',    label: 'Staff Availability', icon: '📆' },
        { id: 'staff-schedule',        label: 'Staff Schedule',     icon: '📅' },
        { id: 'staff-time-management', label: 'Staff Management',   icon: '👤' },
        { id: 'driver-management',     label: 'Driver Management',  icon: '🚐' },
           { id: 'driver-schedule', label: 'Driver Schedule', icon: '🗓️' },
        { id: 'time-reports',          label: 'Time Reports',       icon: '📊' },
        { id: 'incident-report',       label: 'Incident Reports',   icon: '🚨' },
        { id: 'daily-duties',          label: 'Daily Duties',       icon: '📋' },
      ]
    },
    {
      label: 'System', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)',
      items: [
        { id: 'bh-reconcile',    label: 'BH Reconcile', icon: '🔄' },
        { id: 'settings',        label: 'Settings',    icon: '⚙️' },
        { id: 'settings-backup', label: 'Backup Data', icon: '💾' },
      ]
    },
  ];

  const HR_PIN = '4433';
  const HR_PROTECTED = ['locations','programs','staff-availability','staff-schedule','staff-management','driver-management','time-reports'];

const localViews = ['dashboard', 'staff-availability', 'programs', 'driver-management', 'incident-report', 'bh-reconcile', 'daily-duties', 'attendance-report'];

  const handleNavClick = (viewId) => {
    if (HR_PROTECTED.includes(viewId) && !hrUnlocked) {
      setPendingView(viewId);
      setPinInput('');
      setPinError('');
      setShowPinModal(true);
      return;
    }
    setCurrentView(viewId);
    if (!localViews.includes(viewId)) onNavigate(viewId);
  };

  const handlePinSubmit = () => {
    if (pinInput === HR_PIN) {
      setHrUnlocked(true);
      setHrUnlockedAt(Date.now());
      setShowPinModal(false);
      const view = pendingView;
      setPendingView(null);
      setCurrentView(view);
      if (!localViews.includes(view)) onNavigate(view);
    } else {
      setPinError('Incorrect PIN. Try again.');
      setPinInput('');
    }
  };

  const allNavItems = navSections.flatMap(s => s.items);
  const activeSection = navSections.find(s => s.items.some(i => i.id === currentView));
  const currentLabel = allNavItems.find(i => i.id === currentView)?.label || 'Dashboard';

  // Sidebar background — white for Redmond, green for Kirkland, blue for All
  const sidebarBg =
    currentLocation === 'kirkland' ? '#e8fdf2' :
    currentLocation === 'all'      ? '#e8f0fe' :
    '#ffffff'; // plain white for Redmond

  const sidebarBorder =
    currentLocation === 'kirkland' ? '1px solid #bbf7d0' :
    currentLocation === 'all'      ? '1px solid #c7d7f7' :
    '1px solid #e5e7eb'; // subtle grey border for Redmond

  const NavItem = ({ item, sectionColor, sectionBg }) => {
    const isActive = currentView === item.id;
    return (
      <div
        onClick={() => handleNavClick(item.id)}
        style={{
          margin: '1px 8px',
          padding: item.sub ? '0.4rem 0.75rem 0.4rem 2.75rem' : '0.55rem 0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          cursor: 'pointer', borderRadius: '8px', transition: 'all 0.15s',
          position: 'relative',
          backgroundColor: isActive ? sectionBg : 'transparent',
          color: isActive ? '#1a1a1a' : item.sub ? '#4b5563' : '#1a1a1a',
          fontSize: item.sub ? '0.85rem' : '0.95rem',
          fontWeight: isActive ? '700' : '400',
          borderLeft: isActive ? `2.5px solid ${sectionColor}` : '2.5px solid transparent',
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = '#111827'; } }}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = item.sub ? '#4b5563' : '#1a1a1a'; } }}
      >
        {!item.sub && (
          <span style={{
            width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
            backgroundColor: isActive ? sectionColor + '25' : 'rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.85rem', transition: 'all 0.15s',
          }}>
            {item.icon}
          </span>
        )}
        <span style={{ flex: 1 }}>{item.sub ? `· ${item.label}` : item.label}</span>
        {isActive && (
          <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: sectionColor, flexShrink: 0 }} />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100vh' }}>

      {/* ── Sidebar ── */}
      <div style={{
        width: '265px',
        background: sidebarBg,
        borderRight: sidebarBorder,
        color: '#1a1a1a',
        display: 'flex', flexDirection: 'column',
        boxShadow: '4px 0 20px rgba(0,0,0,0.06)',
        position: 'fixed', height: '100vh', overflowY: 'auto',
        transition: 'background 0.4s ease',
      }}>

        {/* Brand + Location Switcher */}
        <div style={{ padding: '1.25rem 1.25rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
              background: 'linear-gradient(135deg, #EE6123 0%, #f59e0b 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.25rem', boxShadow: '0 4px 12px rgba(238,97,35,0.3)',
            }}>🎓</div>
            <div>
              <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#111827', letterSpacing: '-0.02em' }}>Stemtree</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '0.05rem' }}>Admin Portal</div>
            </div>
          </div>

          {/* Location Switcher */}
          <div style={{ marginTop: '0.85rem' }}>
            <button
              onClick={() => setShowLocationPicker(p => !p)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.45rem 0.65rem', backgroundColor: 'rgba(0,0,0,0.04)',
                border: `1.5px solid ${locationColor}60`, borderRadius: '8px',
                cursor: 'pointer', color: '#1a1a1a',
              }}
            >
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: locationColor, flexShrink: 0 }} />
              <span style={{ fontSize: '0.78rem', fontWeight: '700', color: locationColor, flex: 1, textAlign: 'left' }}>
                📍 {locationLabel}
              </span>
              <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{showLocationPicker ? '▲' : '▼'}</span>
            </button>

            {showLocationPicker && (
              <div style={{ marginTop: '0.25rem', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)', backgroundColor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                {Object.values(LOCATIONS).map(loc => (
                  <button key={loc.id}
                    onClick={() => { setCurrentLocation(loc.id); setShowLocationPicker(false); }}
                    style={{
                      width: '100%', padding: '0.6rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem',
                      backgroundColor: currentLocation === loc.id ? `${loc.color}15` : 'transparent',
                      border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', color: '#1a1a1a', textAlign: 'left',
                    }}
                  >
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: loc.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.82rem', fontWeight: currentLocation === loc.id ? '700' : '400', color: currentLocation === loc.id ? loc.color : '#374151' }}>{loc.label}</span>
                    {currentLocation === loc.id && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: loc.color }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Today pill */}
          <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.65rem', backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: '7px', border: '1px solid rgba(0,0,0,0.07)' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#EE6123', flexShrink: 0 }} />
            <span style={{ fontSize: '0.72rem', color: '#374151' }}>
              {todayStats.scheduledToday} students · {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </div>

          {/* HR unlock indicator */}
          {hrUnlocked && (
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.65rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} />
              <span style={{ fontSize: '0.68rem', color: '#15803d', fontWeight: '600' }}>HR unlocked · auto-locks in 30 min</span>
            </div>
          )}
        </div>

        {/* Dashboard link */}
        <div style={{ padding: '0.75rem 0.5rem 0.25rem' }}>
          <div
            onClick={() => handleNavClick('dashboard')}
            style={{
              margin: '1px 8px', padding: '0.55rem 0.75rem',
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              cursor: 'pointer', borderRadius: '8px', transition: 'all 0.15s',
              backgroundColor: currentView === 'dashboard' ? 'rgba(96,165,250,0.12)' : 'transparent',
              color: '#111827',
              fontWeight: currentView === 'dashboard' ? '700' : '400',
              fontSize: '0.855rem',
              borderLeft: currentView === 'dashboard' ? '2.5px solid #60a5fa' : '2.5px solid transparent',
            }}
            onMouseEnter={e => { if (currentView !== 'dashboard') { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; } }}
            onMouseLeave={e => { if (currentView !== 'dashboard') { e.currentTarget.style.backgroundColor = 'transparent'; } }}
          >
            <span style={{ width: '26px', height: '26px', borderRadius: '7px', backgroundColor: currentView === 'dashboard' ? 'rgba(96,165,250,0.2)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', flexShrink: 0 }}>🏠</span>
            <span>Dashboard</span>
          </div>
        </div>

        {/* Sections */}
        <div style={{ flex: 1, padding: '0.25rem 0 1rem', overflowY: 'auto' }}>
          {navSections.map(section => (
            <div key={section.label} style={{ marginBottom: '0.5rem' }}>
              <div style={{ padding: '0.85rem 1.25rem 0.3rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: section.color, flexShrink: 0 }} />
                <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: section.color, fontWeight: '700' }}>
                  {section.label}
                </span>
              </div>
              {section.items.map(item => (
                <NavItem key={item.id} item={item} sectionColor={section.color} sectionBg={section.bg} />
              ))}
            </div>
          ))}
        </div>

        {/* Logout */}
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid rgba(0,0,0,0.07)' }}>
          <button
            onClick={onLogout}
            style={{
              width: '100%', padding: '0.6rem 0.75rem',
              backgroundColor: 'rgba(239,68,68,0.08)', color: '#dc2626',
              border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px',
              cursor: 'pointer', fontWeight: '600', fontSize: '0.83rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#dc2626'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ marginLeft: '265px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>

        {/* Top Bar */}
        <div style={{
          background: 'white', padding: '0.85rem 2rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {activeSection && (
              <div style={{ width: '8px', height: '28px', borderRadius: '4px', backgroundColor: activeSection.color }} />
            )}
            <div>
              <h1 style={{ fontSize: '1.1rem', color: '#0f172a', margin: 0, fontWeight: '700' }}>{currentLabel}</h1>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.05rem' }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
          {todayStats.alerts.length > 0 && (
            <div style={{ fontSize: '0.75rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.3rem 0.75rem', borderRadius: '6px', border: '1px solid #fde68a', fontWeight: '600' }}>
              ⚠️ {todayStats.alerts[0]}
            </div>
          )}
        </div>

        {/* Dashboard */}
        {currentView === 'dashboard' && (
          <div style={{ padding: '1.5rem 2rem', overflowY: 'auto' }}>

            {/* Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', padding: '1.25rem 1.5rem', borderRadius: '14px', boxShadow: '0 4px 16px rgba(59,130,246,0.3)', color: 'white', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>👥</div>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: 1 }}>{todayStats.scheduledToday}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: '0.15rem' }}>Scheduled Today</div>
                </div>
              </div>

              {todayStats.alerts.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #f59e0b, #f59e0b)', padding: '1.25rem 1.5rem', borderRadius: '14px', boxShadow: '0 4px 16px rgba(245,158,11,0.3)', color: 'white', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>⚠️</div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: 1 }}>{todayStats.alerts.length}</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: '0.15rem' }}>Alert{todayStats.alerts.length > 1 ? 's' : ''}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '14px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>Quick Actions</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem' }}>
                {[
                  { view: 'todays-students', gradient: 'linear-gradient(135deg, #3b82f6, #3b82f6)', shadow: 'rgba(39,159,233,0.77)', icon: '📋', label: "Today's Roster",  sub: 'All students scheduled today' },
                  { view: 'front-desk',       gradient: 'linear-gradient(135deg, #047857, #10b981)', shadow: 'rgba(16,185,129,0.35)', icon: '✅', label: 'Front Desk',       sub: 'Check students in & out' },
                  { view: 'staff-schedule',   gradient: 'linear-gradient(135deg, #6d28d9, #8b5cf6)', shadow: 'rgba(139,92,246,0.35)', icon: '📅', label: 'Staff Schedule',   sub: 'View and assign shifts' },
                  { view: 'staff-dashboard',  gradient: 'linear-gradient(135deg, #db8a4c, #f59e0b)', shadow: 'rgba(245,158,11,0.35)', icon: '🚐', label: 'Pickup Monitor',   sub: 'Live driver & student status' },
                ].map(btn => (
                  <button
                    key={btn.view}
                    onClick={() => handleNavClick(btn.view)}
                    style={{ padding: '1rem 1.1rem', background: btn.gradient, color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', boxShadow: `0 4px 14px ${btn.shadow}` }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 20px ${btn.shadow}`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 14px ${btn.shadow}`; }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{btn.icon}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', marginBottom: '0.2rem' }}>{btn.label}</div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.8 }}>{btn.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* System Status */}
            <div style={{ background: 'white', padding: '1.25rem 1.5rem', borderRadius: '14px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>System Status</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0, boxShadow: '0 0 6px #22c55e' }} />
                <span style={{ fontSize: '0.875rem', color: '#15803d', fontWeight: '500' }}>All systems operational — {todayStats.scheduledToday} students scheduled for today.</span>
              </div>
            </div>
          </div>
        )}

        {currentView === 'staff-availability' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <StaffAvailability onBack={() => setCurrentView('dashboard')} />
          </div>
        )}

        {currentView === 'programs' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <ProgramManagement onBack={() => setCurrentView('dashboard')} />
          </div>
        )}

        {currentView === 'driver-management' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <DriverManagement onBack={() => setCurrentView('dashboard')} />
          </div>
        )}

           {currentView === 'driver-schedule' && (
     <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
       <WeeklyDriverAssignment onBack={() => setCurrentView('dashboard')} />
     </div>
   )}

        {currentView === 'incident-report' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <IncidentReport onBack={() => setCurrentView('dashboard')} />
          </div>
        )}

        {currentView === 'bh-reconcile' && (
  <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
    <BHReconcile onBack={() => setCurrentView('dashboard')} />
  </div>
)}

        {currentView === 'daily-duties' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <DailyDuties onBack={() => setCurrentView('dashboard')} />
          </div>
        )}

        {currentView === 'attendance-report' && (
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f1f5f9' }}>
            <AttendanceReport onBack={() => setCurrentView('dashboard')} />
          </div>
        )}
      </div>

      {/* HR PIN Modal */}
      {showPinModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '14px', padding: '2rem', width: '90%', maxWidth: '360px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔒</div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1f2937', margin: '0 0 0.25rem' }}>Staff & Ops Access</h2>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1.5rem' }}>Enter your HR PIN to continue</p>
            <input
              type="password" value={pinInput}
              onChange={e => { setPinInput(e.target.value); setPinError(''); }}
              onKeyDown={e => e.key === 'Enter' && handlePinSubmit()}
              placeholder="Enter PIN" autoFocus maxLength={6}
              style={{ width: '100%', padding: '0.85rem', fontSize: '1.2rem', textAlign: 'center', letterSpacing: '0.3em', border: `2px solid ${pinError ? '#dc2626' : '#d1d5db'}`, borderRadius: '10px', outline: 'none', boxSizing: 'border-box', marginBottom: '0.5rem' }}
            />
            {pinError && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{pinError}</p>}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={() => { setShowPinModal(false); setPendingView(null); }} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handlePinSubmit} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>Unlock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardSidebar;